import { Link } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { SeoTags } from "../../components/Head/SeoTags";

const APP_URL = process.env.REACT_APP_APP_URL;

const STATS = ["encryption", "zero", "unlimited", "browsers"];

const VALUES = [
  { icon: "bi-unlock", key: "freedom" },
  { icon: "bi-lightbulb", key: "intelligent" },
  { icon: "bi-shield-check", key: "privacy" },
];

const DIFFERENTIATORS = [
  "browserIndependent",
  "multiCollection",
  "aiLearns",
  "zeroKnowledge",
];

const About = () => {
  const { t } = useTranslation();

  return (
    <>
      <SeoTags
        title={t("about.seo.title")}
        description={t("about.seo.description")}
        keywords={t("about.seo.keywords")}
        path="/about"
      />

      {/* Organization Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "onBookmarks",
            url: "https://onbookmarks.com",
            logo: "https://onbookmarks.com/images/icons/logo-only.svg",
            description: t("about.schema.orgDescription"),
            foundingDate: "2024",
            contactPoint: {
              "@type": "ContactPoint",
              contactType: "customer support",
              email: "support@onbookmarks.com",
            },
            knowsAbout: [
              "Bookmark Management",
              "Digital Organization",
              "Privacy Technology",
              "Client-side Encryption",
              "AI Tagging",
              "Cross-platform Synchronization",
            ],
          }),
        }}
      />

      {/* AboutPage Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AboutPage",
            name: t("about.schema.aboutName"),
            description: t("about.schema.aboutDescription"),
            url: "https://onbookmarks.com/about",
            mainEntity: {
              "@type": "Organization",
              name: "onBookmarks",
              mission: t("about.schema.mission"),
            },
            isPartOf: {
              "@type": "WebSite",
              name: "onBookmarks",
              url: "https://onbookmarks.com",
            },
          }),
        }}
      />

      {/* Breadcrumb Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: t("about.schema.home"),
                item: "https://onbookmarks.com",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: t("about.schema.about"),
                item: "https://onbookmarks.com/about",
              },
            ],
          }),
        }}
      />

      {/* Hero Section */}
      <section className="hero bg-light">
        <div className="container">
          <div className="row align-items-center min-vh-50">
            <div className="col-lg-8 mx-auto text-center">
              <h1 className="display-4 fw-bold mb-4">
                <Trans
                  i18nKey="about.hero.title"
                  components={{ 1: <span className="text-primary-gradient" /> }}
                />
              </h1>
              <p className="lead">{t("about.hero.subtitle")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-5">
        <div className="container">
          <div className="row">
            <div className="col-lg-10 mx-auto">
              <div className="row align-items-center">
                <div className="col-lg-6 mb-4 mb-lg-0">
                  <h2 className="h1 fw-bold mb-4">{t("about.story.title")}</h2>
                  <p className="mb-4">{t("about.story.p1")}</p>
                  <p className="mb-4">{t("about.story.p2")}</p>
                  <p className="mb-4">{t("about.story.p3")}</p>
                  <p className="fw-medium text-primary">
                    {t("about.story.p4")}
                  </p>
                </div>
                <div className="col-lg-6">
                  <div className="position-relative">
                    <div className="bg-primary bg-opacity-10 rounded-4 p-5">
                      <div className="row g-4 text-center">
                        {STATS.map((s) => (
                          <div className="col-6" key={s}>
                            <h3 className="display-6 fw-bold text-primary mb-2">
                              {t(`about.story.stats.${s}.value`)}
                            </h3>
                            <p className="text-muted mb-0">
                              {t(`about.story.stats.${s}.label`)}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Values */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="h1 fw-bold mb-4">{t("about.values.title")}</h2>
            <p className="lead text-muted">{t("about.values.subtitle")}</p>
          </div>

          <div className="row g-4">
            {VALUES.map((v) => (
              <div className="col-md-4" key={v.key}>
                <div className="card h-100 border-0 shadow-sm">
                  <div className="card-body p-4 text-center">
                    <div className="icon icon-lg bg-primary bg-opacity-10 rounded-3 mb-3 mx-auto">
                      <i className={`bi ${v.icon} fs-3 text-primary`}></i>
                    </div>
                    <h3 className="h5 mb-3">
                      {t(`about.values.${v.key}.title`)}
                    </h3>
                    <p className="text-muted mb-0">
                      {t(`about.values.${v.key}.text`)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What Makes Us Different */}
      <section className="py-5">
        <div className="container">
          <div className="row">
            <div className="col-lg-10 mx-auto">
              <div className="text-center mb-5">
                <h2 className="h1 fw-bold mb-4">{t("about.different.title")}</h2>
                <p className="lead text-muted">
                  {t("about.different.subtitle")}
                </p>
              </div>

              <div className="row g-4">
                {DIFFERENTIATORS.map((d) => (
                  <div className="col-lg-6" key={d}>
                    <div className="d-flex align-items-start">
                      <div className="icon icon-sm bg-success bg-opacity-10 rounded-3 me-3 mt-1">
                        <i className="bi bi-check-lg text-success"></i>
                      </div>
                      <div>
                        <h4 className="h6 mb-2">
                          {t(`about.different.${d}.title`)}
                        </h4>
                        <p className="text-muted mb-0">
                          {t(`about.different.${d}.text`)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-5">
        <div className="container">
          <div className=" banner-gradient rounded-4 p-5 text-center text-white">
            <h2 className="h1 fw-bold mb-4 text-white">{t("about.cta.title")}</h2>
            <p className="lead mb-4">{t("about.cta.description")}</p>
            <div className="d-flex gap-3 justify-content-center flex-wrap">
              <Link
                to={`${APP_URL}/register`}
                className="btn btn-light btn-lg text-muted"
              >
                {t("about.cta.tryButton")}
              </Link>
              <a
                href="mailto:support@onbookmarks.com"
                className="btn btn-outline-light btn-lg"
              >
                {t("about.cta.contactButton")}
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default About;
